import React, { useMemo, useState } from 'react';
import {
  Plus,
  ChevronLeft,
  ChevronRight,
  Edit3,
  Trash2,
  PlayCircle,
  CheckCircle,
  AlertTriangle,
  CalendarDays
} from 'lucide-react';
import ResponsiveModalWrapper from '../modals/ResponsiveModalWrapper';
import { toDateKey, parseDateKey } from '../utils/date';

// La semana empieza en lunes (Honduras)
const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const SOFT_SHADOW =
  'shadow-[0_1px_2px_rgba(62,39,35,0.05),0_10px_24px_-14px_rgba(62,39,35,0.18)]';
const FOCUS = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sol-400/70';

const PRIORITY = {
  high: { text: 'Alta', bar: 'bg-secondary-700' },
  medium: { text: 'Media', bar: 'bg-sol-400' },
  low: { text: 'Baja', bar: 'bg-green-600' }
};

const STATUS = {
  pending: { text: 'Pendiente', chip: 'bg-neutral-50/60 text-neutral-400', dot: 'bg-neutral-200' },
  inProgress: { text: 'En progreso', chip: 'bg-sol-200/25 text-cafe-600', dot: 'bg-sol-400' },
  completed: { text: 'Realizada', chip: 'bg-green-50 text-green-700', dot: 'bg-green-600' }
};

const pad = (n) => String(n).padStart(2, '0');

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

const CalendarView = ({
  selectedDate,
  setSelectedDate,
  setShowTaskModal,
  tasks,
  toggleTask,
  deleteTask,
  editTask,
  changeTaskStatus
}) => {
  const [taskToDelete, setTaskToDelete] = useState(null);

  const year = selectedDate.getFullYear();
  const month = selectedDate.getMonth();
  const todayKey = toDateKey(new Date());
  const selectedKey = toDateKey(selectedDate);

  const tasksByDate = useMemo(() => {
    const map = new Map();
    tasks.forEach((task) => {
      const list = map.get(task.date) || [];
      list.push(task);
      map.set(task.date, list);
    });
    return map;
  }, [tasks]);

  // Solo las semanas que el mes necesita (5 o 6), con el día 1 en su columna real
  const cells = useMemo(() => {
    // getDay() cuenta desde el domingo (0); se corre para que el lunes sea la columna 0
    const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const total = Math.ceil((firstWeekday + daysInMonth) / 7) * 7;
    return Array.from({ length: total }, (_, i) => new Date(year, month, i - firstWeekday + 1));
  }, [year, month]);

  const monthPrefix = `${year}-${pad(month + 1)}`;
  const monthTaskCount = tasks.filter(
    (task) => typeof task.date === 'string' && task.date.startsWith(monthPrefix)
  ).length;

  // Las realizadas al final de la lista del día
  const dayTasks = useMemo(() => {
    const list = tasksByDate.get(selectedKey) || [];
    return [...list].sort((a, b) => Number(a.status === 'completed') - Number(b.status === 'completed'));
  }, [tasksByDate, selectedKey]);

  const pluralTasks = (count) => `${count} ${count === 1 ? 'tarea' : 'tareas'}`;

  const executeDelete = () => {
    if (taskToDelete) {
      deleteTask(taskToDelete.id);
      setTaskToDelete(null);
    }
  };

  const handleTaskToggle = (e, taskId, action = 'toggle') => {
    e.stopPropagation();
    e.preventDefault();

    if (action === 'toggle') {
      toggleTask(taskId);
    } else {
      changeTaskStatus(taskId, action);
    }
  };

  const handleTaskEdit = (e, task) => {
    e.stopPropagation();
    e.preventDefault();
    editTask(task);
  };

  const handleTaskDelete = (e, task) => {
    e.stopPropagation();
    e.preventDefault();
    setTaskToDelete(task);
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Encabezado */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="font-display text-3xl font-semibold leading-tight tracking-tight text-cafe-700 sm:text-4xl">
            Calendario
          </h2>
          <p className="mt-1 text-sm text-neutral-400">Organice y dé seguimiento a sus tareas por día.</p>
        </div>
        <button
          onClick={() => setShowTaskModal(true)}
          className={`inline-flex items-center justify-center gap-2 rounded-xl bg-[#5D4037] px-5 py-2.5 text-sm font-semibold text-white ${SOFT_SHADOW} transition-colors duration-300 hover:bg-[#6D4C41] ${FOCUS}`}
        >
          <Plus className="h-4 w-4" />
          Nueva tarea
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Calendario */}
        <section className="rounded-2xl bg-cafe-100/25 p-4 sm:p-6 lg:col-span-2">
          <div className="mb-5 flex items-center justify-between gap-4">
            <div>
              <h3 className="font-display text-2xl font-semibold tracking-tight text-cafe-700">
                {capitalize(selectedDate.toLocaleDateString('es-CO', { month: 'long' }))}{' '}
                <span className="font-normal text-cafe-400">{year}</span>
              </h3>
              <p className="mt-0.5 text-xs text-neutral-400">
                {monthTaskCount === 0 ? 'Sin tareas este mes' : `${pluralTasks(monthTaskCount)} este mes`}
              </p>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => setSelectedDate(new Date(year, month - 1, 1))}
                aria-label="Mes anterior"
                className={`rounded-lg p-2 text-cafe-600 transition-colors duration-200 hover:bg-white touch-manipulation ${FOCUS}`}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => setSelectedDate(new Date())}
                className={`rounded-lg px-3 py-2 text-sm font-medium text-cafe-700 transition-colors duration-200 hover:bg-white ${FOCUS}`}
              >
                Hoy
              </button>
              <button
                onClick={() => setSelectedDate(new Date(year, month + 1, 1))}
                aria-label="Mes siguiente"
                className={`rounded-lg p-2 text-cafe-600 transition-colors duration-200 hover:bg-white touch-manipulation ${FOCUS}`}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="mb-2 grid grid-cols-7 gap-1.5 sm:gap-2">
            {WEEKDAYS.map((day) => (
              <div key={day} className="py-1 text-center text-xs font-medium text-neutral-400">
                <span className="hidden sm:inline">{day}</span>
                <span className="sm:hidden">{day.charAt(0)}</span>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
            {cells.map((date) => {
              const key = toDateKey(date);
              const isCurrentMonth = date.getMonth() === month;
              const isToday = key === todayKey;
              const isSelected = key === selectedKey;
              const cellTasks = tasksByDate.get(key) || [];

              return (
                <button
                  key={key}
                  onClick={() => setSelectedDate(date)}
                  aria-pressed={isSelected}
                  aria-current={isToday ? 'date' : undefined}
                  aria-label={`${date.toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })}${
                    cellTasks.length ? `, ${pluralTasks(cellTasks.length)}` : ''
                  }`}
                  className={`relative flex min-h-[3.25rem] flex-col items-start justify-between rounded-xl p-2 text-left text-sm transition-colors duration-200 sm:min-h-[4.5rem] sm:p-2.5 touch-manipulation ${FOCUS} ${
                    isSelected
                      ? 'bg-[#5D4037] font-semibold text-white shadow-md'
                      : isCurrentMonth
                      ? 'bg-white/80 text-cafe-800 hover:bg-white hover:shadow-md'
                      : 'bg-white/30 text-neutral-200 hover:bg-white/60 hover:text-neutral-300'
                  } ${isToday ? 'ring-2 ring-sol-400' : ''}`}
                >
                  <span className={isToday && !isSelected ? 'font-bold text-cafe-700' : ''}>{date.getDate()}</span>

                  {cellTasks.length > 0 && (
                    <span className="flex items-center gap-0.5 sm:gap-1">
                      {cellTasks.slice(0, 3).map((task) => (
                        <span
                          key={task.id}
                          className={`h-1.5 w-1.5 rounded-full ${(STATUS[task.status] || STATUS.pending).dot}`}
                        />
                      ))}
                      {cellTasks.length > 3 && (
                        <span className="text-[10px] leading-none opacity-70">+{cellTasks.length - 3}</span>
                      )}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-neutral-400">
            {Object.values(STATUS).map((status) => (
              <span key={status.text} className="flex items-center gap-1.5">
                <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
                {status.text}
              </span>
            ))}
          </div>
        </section>

        {/* Tareas del día */}
        <aside className="rounded-2xl bg-cafe-100/25 p-4 sm:p-6">
          <div className="mb-5">
            <h3 className="font-display text-xl font-semibold tracking-tight text-cafe-700 first-letter:uppercase">
              {selectedDate.toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })}
            </h3>
            <p className="mt-0.5 text-xs text-neutral-400">
              {dayTasks.length === 0 ? 'Sin tareas' : pluralTasks(dayTasks.length)}
            </p>
          </div>

          {dayTasks.length > 0 ? (
            <ul className="max-h-[32rem] space-y-3 overflow-y-auto pr-1">
              {dayTasks.map((task) => {
                const priority = PRIORITY[task.priority];
                const status = STATUS[task.status] || STATUS.pending;
                const isDone = task.status === 'completed' || task.completed;

                return (
                  <li
                    key={task.id}
                    className={`group relative select-none rounded-xl bg-white py-3 pl-5 pr-3 ${SOFT_SHADOW}`}
                  >
                    {priority && (
                      <span
                        className={`absolute bottom-3 left-0 top-3 w-0.5 rounded-full ${priority.bar}`}
                        title={`Prioridad ${priority.text.toLowerCase()}`}
                      />
                    )}

                    <div className="flex items-start justify-between gap-2">
                      <p
                        className={`min-w-0 text-sm font-medium leading-snug ${
                          isDone ? 'text-neutral-300 line-through' : 'text-cafe-800'
                        }`}
                      >
                        {task.text}
                      </p>

                      <div className="flex flex-shrink-0 items-center opacity-100 transition-opacity sm:opacity-0 sm:group-focus-within:opacity-100 sm:group-hover:opacity-100">
                        <button
                          onClick={(e) => handleTaskEdit(e, task)}
                          title="Editar tarea"
                          aria-label="Editar tarea"
                          className={`rounded-lg p-1.5 text-neutral-400 transition-colors hover:bg-cafe-100/50 hover:text-cafe-700 touch-manipulation ${FOCUS}`}
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => handleTaskDelete(e, task)}
                          title="Eliminar tarea"
                          aria-label="Eliminar tarea"
                          className={`rounded-lg p-1.5 text-neutral-400 transition-colors hover:bg-red-50 hover:text-red-700 touch-manipulation ${FOCUS}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <div className="mt-2 flex items-center justify-between gap-2">
                      <span className={`rounded-md px-2 py-0.5 text-xs font-medium ${status.chip}`}>{status.text}</span>
                      {priority && <span className="text-xs text-neutral-400">Prioridad {priority.text.toLowerCase()}</span>}
                    </div>

                    {!isDone && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {task.status === 'pending' && (
                          <button
                            onClick={(e) => handleTaskToggle(e, task.id, 'inProgress')}
                            className={`inline-flex items-center gap-1.5 rounded-lg bg-sol-200/25 px-3 py-1.5 text-xs font-semibold text-cafe-600 transition-colors hover:bg-sol-200/40 touch-manipulation ${FOCUS}`}
                          >
                            <PlayCircle className="h-3.5 w-3.5" />
                            Iniciar
                          </button>
                        )}
                        <button
                          onClick={(e) => handleTaskToggle(e, task.id, 'completed')}
                          className={`inline-flex items-center gap-1.5 rounded-lg bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-700 transition-colors hover:bg-green-100 touch-manipulation ${FOCUS}`}
                        >
                          <CheckCircle className="h-3.5 w-3.5" />
                          Completar
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="py-10 text-center">
              <div className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white text-cafe-500 ${SOFT_SHADOW}`}>
                <CalendarDays className="h-6 w-6" />
              </div>
              <h4 className="font-display text-lg font-semibold text-cafe-700">Sin tareas este día</h4>
              <p className="mt-1 text-sm text-neutral-400">Agregue una tarea para organizarlo.</p>
              <button
                onClick={() => setShowTaskModal(true)}
                className={`mt-4 rounded-lg px-3 py-1.5 text-sm font-semibold text-cafe-700 underline-offset-4 hover:underline ${FOCUS}`}
              >
                Agregar tarea
              </button>
            </div>
          )}
        </aside>
      </div>

      {/* Confirmación de eliminación */}
      <ResponsiveModalWrapper
        show={Boolean(taskToDelete)}
        onClose={() => setTaskToDelete(null)}
        title="Eliminar Tarea"
        icon={<AlertTriangle className="h-6 w-6 text-red-600" />}
        maxWidth="max-w-md"
        closeOnEscape={true}
        closeOnOverlay={true}
        customHeaderBg="#7B5B3E"
      >
        {taskToDelete && (
          <div className="p-4 sm:p-6">
            <div className="mb-6 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 sm:h-20 sm:w-20">
                <AlertTriangle className="h-8 w-8 text-red-600 sm:h-10 sm:w-10" />
              </div>
              <p className="mb-4 text-sm text-gray-600 sm:text-base">¿Está seguro que desea eliminar esta tarea?</p>
              <div className="select-none rounded-xl bg-gray-50 p-3 sm:p-4">
                <p className="text-sm font-semibold text-gray-900 sm:text-base">"{taskToDelete.text}"</p>
                <p className="mt-1 text-xs text-gray-600 sm:text-sm">
                  Fecha: {parseDateKey(taskToDelete.date).toLocaleDateString('es-CO')}
                </p>
              </div>
            </div>
            <div className="flex flex-col space-y-3 sm:flex-row sm:space-x-4 sm:space-y-0">
              <button
                onClick={() => setTaskToDelete(null)}
                className="flex-1 rounded-xl bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-200 sm:rounded-2xl sm:px-6 sm:py-3 sm:text-base"
              >
                Cancelar
              </button>
              <button
                onClick={executeDelete}
                className="flex-1 rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-red-700 sm:rounded-2xl sm:px-6 sm:py-3 sm:text-base"
              >
                Eliminar Tarea
              </button>
            </div>
          </div>
        )}
      </ResponsiveModalWrapper>
    </div>
  );
};

export default CalendarView;
